import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { ItineraryCard } from '../components/ItineraryCard';
import { AffiliateDisclosure } from '../components/AffiliateDisclosure';
import { Seo } from '../components/Seo';
import { breadcrumb } from '../lib/structuredData';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { api } from '../lib/api';
import { useApp } from '../context/AppContext';
import { Compass, Search, MapPin, Clock, Sparkles, ArrowRight } from 'lucide-react';

const SORTS = [['trending', 'Trending'], ['most_viewed', 'Most viewed'], ['most_saved', 'Most saved'], ['newest', 'Newest']];

export default function Itineraries() {
  const { currency } = useApp();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [tripType, setTripType] = useState('');
  const [sort, setSort] = useState('trending');
  const [category, setCategory] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.discoverItineraries({ q, trip_type: tripType, sort, destination: category });
      setData(data);
    } catch (e) { console.error('Failed to load itineraries', e); } finally { setLoading(false); }
  }, [q, tripType, sort, category]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const [editorial, setEditorial] = useState([]);
  useEffect(() => { api.editorialList().then(({ data }) => setEditorial(data.itineraries || [])).catch(() => setEditorial([])); }, []);
  const featured = editorial.filter((c) => {
    const ql = q.trim().toLowerCase(); const cl = category.trim().toLowerCase();
    if (ql && !(c.keywords || '').includes(ql)) return false;
    if (cl && !(c.keywords || '').includes(cl)) return false;
    if (tripType && c.tripType !== tripType) return false;
    return true;
  });

  return (
    <Layout>
      <Seo title="Creator Travel Itineraries"
        description="Discover hand-crafted, day-by-day travel itineraries created by real travellers and creators. Find trips to Tokyo, Bali, Europe and more on FlightDeals.in."
        path="/itineraries"
        jsonLd={breadcrumb([{ name: 'Home', path: '/' }, { name: 'Itineraries' }])} />
      <section className="relative">
        <div className="absolute inset-0 h-64"><img src="https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?w=1400&q=80" alt="" className="w-full h-full object-cover" /><div className="absolute inset-0 hero-overlay" /></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-6 text-primary-foreground">
          <span className="inline-flex items-center gap-1.5 text-accent text-sm font-semibold"><Compass className="h-4 w-4" /> Creator Itineraries</span>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl font-semibold max-w-2xl">Travel better with itineraries created by people who actually travel.</h1>
          <div className="mt-5 max-w-xl relative">
            <Search className="h-4 w-4 absolute left-3 top-3.5 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search itineraries, e.g. Tokyo, Bali..." className="pl-9 h-11 bg-card text-foreground" data-testid="itinerary-search-input" />
          </div>
        </div>
      </section>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2" data-testid="itinerary-categories">
          {['', ...(data?.categories || [])].map((c) => (
            <button key={c || 'all'} onClick={() => setCategory(c)} className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap border transition-colors ${category === c ? 'bg-accent/12 text-accent border-accent/30' : 'bg-card text-muted-foreground border-border hover:bg-secondary'}`} data-testid={`itcat-${(c || 'all').toLowerCase().replace(/ /g, '-')}`}>{c || 'All'}</button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2 mt-3 mb-4">
          {SORTS.map(([v, l]) => (
            <button key={v} onClick={() => setSort(v)} className={`px-3 py-1 rounded-full text-xs font-medium border ${sort === v ? 'bg-primary text-primary-foreground border-primary' : 'bg-card text-muted-foreground border-border hover:bg-secondary'}`} data-testid={`itsort-${v}`}>{l}</button>
          ))}
        </div>
        <div className="mb-4"><AffiliateDisclosure variant="banner" /></div>

        {/* Featured editorial guides (house-authored) — filtered by search/category */}
        {featured.length > 0 && (
        <div className="mb-8" data-testid="featured-itineraries">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="h-4 w-4 text-accent" />
            <h2 className="font-display font-semibold text-lg">Featured guides</h2>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {featured.map((c) => (
              <Link key={c.slug} to={`/itineraries/${c.slug}`}
                className="group rounded-2xl border border-border bg-card overflow-hidden card-elevate flex flex-col" data-testid={`featured-itinerary-${c.slug}`}>
                <div className="h-48 relative overflow-hidden">
                  <img src={c.cover} alt={c.coverAlt} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary/80 to-transparent" />
                  <Badge className="absolute top-2 left-2 bg-accent text-accent-foreground rounded-full text-[10px]">{c.badge}</Badge>
                  <div className="absolute bottom-2 left-3 right-3 text-primary-foreground">
                    <p className="font-display font-semibold leading-tight line-clamp-2">{c.title}</p>
                  </div>
                </div>
                <div className="p-4 flex flex-col flex-1">
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {c.destination}</span>
                    <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {c.durationDays}d</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{c.short}</p>
                  <span className="mt-auto pt-3 text-accent font-semibold text-sm inline-flex items-center gap-1">Read guide <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" /></span>
                </div>
              </Link>
            ))}
          </div>
        </div>
        )}

        {loading && <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{[1,2,3,4,5,6].map((i)=><Skeleton key={i} className="h-72 rounded-2xl" />)}</div>}
        {!loading && data && (
          <>
            <p className="text-sm text-muted-foreground mb-3"><span className="font-semibold text-foreground">{data.total}</span> published itineraries</p>
            {data.itineraries.length === 0 ? (
              <div className="text-center py-20 rounded-2xl border border-dashed border-border"><Compass className="h-10 w-10 mx-auto mb-2 opacity-40" /><p className="font-medium">No itineraries yet</p><p className="text-sm text-muted-foreground">Be the first — <a href="/creators/join" className="text-accent">become a creator</a>.</p></div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="itineraries-grid">
                {data.itineraries.map((it) => <ItineraryCard key={it.itinerary_id} it={it} currency={currency} />)}
              </div>
            )}
          </>
        )}
      </section>
    </Layout>
  );
}
